import { useMemo } from "react";
import { useUser } from "@/contexts/user-provider";
import { useLocations } from "@/hooks/use-locations";
import { parseError } from "@/lib/parse-error";
import { toast } from "@/lib/use-toast";
import { SSOUrlRequest } from "@/types/dosespot";
import { PatientDetail } from "@/types/patient";
import { generateDoseSpotSSOUrl } from "@/api/dosespot/sso";
interface UseLaunchDoseSpotOptions {
  /** Location ID - optional. Will be automatically determined if not provided */
  locationId?: string;
  /** Patient object - optional, used to determine primary location if locationId not provided */
  patient?: PatientDetail;
  /** Optional DoseSpot User ID. If not provided, will be looked up from the current provider's dosespot_user_id field */
  userId?: string | null;
  /** Optional User ID when prescribing on behalf of another provider */
  onBehalfOfUserId?: string | null;
  /** Optional Encounter ID to link prescription to specific encounter */
  encounterId?: string | null;
  /** Optional Supervisor User ID for supervision workflows */
  supervisorId?: string | null;
  /** If true, shows refill errors screen */
  refillsErrors?: boolean;
  /** Open in new tab vs current window */
  openInNewTab?: boolean;
}

/**
 * Hook for launching DoseSpot via SSO
 *
 * Returns a function that can be called to launch DoseSpot for a patient.
 * The function automatically determines the best location to use based on:
 * 1. Explicitly provided locationId
 * 2. Current location from user context (if it has DoseSpot configured)
 * 3. Patient's primary location (if it has DoseSpot configured)
 * 4. First available location with DoseSpot configured
 */
export function useLaunchDoseSpot(options: UseLaunchDoseSpotOptions = {}) {
  const {
    locationId: providedLocationId,
    patient,
    userId,
    onBehalfOfUserId,
    encounterId,
    supervisorId,
    refillsErrors = false,
    openInNewTab = true
  } = options;
  const {
    currentLocationId,
    currentUser
  } = useUser();
  const {
    locations
  } = useLocations();

  /**
   * Get all locations with DoseSpot configured
   */
  const dosespotLocations = useMemo(() => {
    if (!locations) return [];
    return locations.filter(loc => loc.dosespotClinicId && loc.dosespotClinicKey);
  }, [locations]);

  /**
   * Automatically determine the best location ID to use for DoseSpot.
   * Priority order:
   * 1. Explicitly provided locationId
   * 2. Current location from user context (if it has DoseSpot configured)
   * 3. Patient's primary location (if it has DoseSpot configured)
   * 4. First available location with DoseSpot configured
   */
  const getBestLocationId = (): string | null => {
    // 1. Use explicitly provided locationId
    if (providedLocationId) {
      const hasDoseSpot = dosespotLocations.some(loc_0 => loc_0.id === providedLocationId);
      if (hasDoseSpot) {
        return providedLocationId;
      }
    }

    // 2. Use current location from user context (if it has DoseSpot configured)
    if (currentLocationId) {
      const hasDoseSpot_0 = dosespotLocations.some(loc_1 => loc_1.id === currentLocationId);
      if (hasDoseSpot_0) {
        return currentLocationId;
      }
    }

    // 3. Use patient's primary location (if it has DoseSpot configured)
    if (patient?.primaryLocationId) {
      const hasDoseSpot_1 = dosespotLocations.some(loc_2 => loc_2.id === patient.primaryLocationId);
      if (hasDoseSpot_1) {
        return patient.primaryLocationId;
      }
    }

    // 4. Use first available location with DoseSpot
    if (dosespotLocations.length > 0) {
      return dosespotLocations[0].id;
    }
    return null;
  };

  /**
   * Launch DoseSpot for a patient or without a patient (dashboard view).
   * When patientId is provided, the backend always syncs current demographics first.
   * @param patientId - Optional patient ID to launch DoseSpot for. If not provided, opens DoseSpot dashboard.
   * @param overrideLocationId - Optional location ID to override the automatic selection
   */
  const launchDoseSpot = async (patientId?: string | null, overrideLocationId?: string) => {
    // Check if any locations have DoseSpot configured
    if (dosespotLocations.length === 0) {
      toast({
        title: "Error",
        description: "No location with e-prescribing configured found. Please configure e-prescribing credentials in location settings.",
        variant: "error"
      });
      return;
    }

    // Determine the best location to use (use override if provided)
    const resolvedLocationId = overrideLocationId || getBestLocationId();

    // Safety check: if no override was provided and getBestLocationId returns null,
    // or if override was provided but somehow invalid, show error
    if (!resolvedLocationId) {
      toast({
        title: "Error",
        description: "No location with e-prescribing configured found. Please configure e-prescribing credentials in location settings.",
        variant: "error"
      });
      return;
    }

    // Open the tab synchronously on the user click. Patient-scoped SSO now always
    // awaits a DoseSpot create/update, which can take long enough that a later
    // window.open is treated as non-user-initiated and blocked (same pattern as
    // Junction lab ordering and Google Calendar OAuth).
    let popup: Window | null = null;
    if (openInNewTab) {
      popup = window.open("about:blank", "_blank");
      if (!popup) {
        toast({
          title: "Allow pop-ups to open e-prescribing",
          description: "Your browser blocked the e-prescribing tab. Enable pop-ups for this site and try again.",
          variant: "warning"
        });
        return;
      }
      // Keep our handle so we can redirect after SSO, but drop the reverse link
      // so DoseSpot cannot navigate/script this tab (reverse tabnabbing).
      popup.opener = null;
    }
    try {
      const request: SSOUrlRequest = {
        locationId: resolvedLocationId,
        patientId: patientId || null,
        userId: userId || currentUser?.dosespotUserId || null,
        onBehalfOfUserId: onBehalfOfUserId || null,
        encounterId: encounterId || null,
        supervisorId: supervisorId || null,
        refillsErrors
      };
      const response = await generateDoseSpotSSOUrl(request);
      if (!response?.url) {
        throw new Error("Failed to generate SSO URL");
      }
      if (openInNewTab) {
        if (popup && !popup.closed) {
          popup.location.href = response.url;
        } else {
          // User closed the placeholder tab, or the original open was blocked
          // after we somehow got a null handle past the check above.
          window.open(response.url, "_blank", "noopener,noreferrer");
        }
      } else {
        window.location.href = response.url;
      }
    } catch (error) {
      if (popup && !popup.closed) {
        popup.close();
      }
      console.error("Failed to launch DoseSpot:", error);
      const errorMessage = await parseError(error);
      toast({
        title: "Error Opening E-prescribing",
        description: errorMessage,
        variant: "error"
      });
    }
  };
  return {
    launchDoseSpot,
    dosespotLocations,
    getBestLocationId
  };
}